import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import type { GuitarInputMode } from "../lib/songDetectionProfile";

const STORAGE_KEY_MODE = "bordona.guitarInput.mode";
const STORAGE_KEY_DEVICE = "bordona.guitarInput.deviceId";

export interface AudioInputDevice {
  deviceId: string;
  label: string;
}

interface GuitarInputContextValue {
  inputMode: GuitarInputMode;
  setInputMode: (mode: GuitarInputMode) => void;
  deviceId: string | null;
  setDeviceId: (id: string | null) => void;
  audioInputs: AudioInputDevice[];
  refreshAudioInputs: () => Promise<void>;
}

const GuitarInputContext = createContext<GuitarInputContextValue>({
  inputMode: "mic",
  setInputMode: () => {},
  deviceId: null,
  setDeviceId: () => {},
  audioInputs: [],
  refreshAudioInputs: async () => {},
});

export const GuitarInputProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [inputMode, setInputModeState] = useState<GuitarInputMode>(() => {
    try {
      const v = localStorage.getItem(STORAGE_KEY_MODE);
      return v === "line" ? "line" : "mic";
    } catch {
      return "mic";
    }
  });

  const [deviceId, setDeviceIdState] = useState<string | null>(() => {
    try {
      return localStorage.getItem(STORAGE_KEY_DEVICE);
    } catch {
      return null;
    }
  });

  const [audioInputs, setAudioInputs] = useState<AudioInputDevice[]>([]);

  const setInputMode = useCallback((mode: GuitarInputMode) => {
    setInputModeState(mode);
    try {
      localStorage.setItem(STORAGE_KEY_MODE, mode);
    } catch {
      /* ignore */
    }
  }, []);

  const setDeviceId = useCallback((id: string | null) => {
    setDeviceIdState(id);
    try {
      if (id) localStorage.setItem(STORAGE_KEY_DEVICE, id);
      else localStorage.removeItem(STORAGE_KEY_DEVICE);
    } catch {
      /* ignore */
    }
  }, []);

  const refreshAudioInputs = useCallback(async () => {
    if (!navigator.mediaDevices?.enumerateDevices) return;
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const inputs = devices
        .filter((d) => d.kind === "audioinput")
        .map((d, i) => ({
          deviceId: d.deviceId,
          label: d.label || `Entrada de audio ${i + 1}`,
        }));
      setAudioInputs(inputs);
    } catch {
      setAudioInputs([]);
    }
  }, []);

  useEffect(() => {
    void refreshAudioInputs();
    navigator.mediaDevices?.addEventListener("devicechange", refreshAudioInputs);
    return () => {
      navigator.mediaDevices?.removeEventListener("devicechange", refreshAudioInputs);
    };
  }, [refreshAudioInputs]);

  const value = useMemo(
    () => ({
      inputMode,
      setInputMode,
      deviceId,
      setDeviceId,
      audioInputs,
      refreshAudioInputs,
    }),
    [inputMode, setInputMode, deviceId, setDeviceId, audioInputs, refreshAudioInputs]
  );

  return (
    <GuitarInputContext.Provider value={value}>{children}</GuitarInputContext.Provider>
  );
};

export function useGuitarInput(): GuitarInputContextValue {
  return useContext(GuitarInputContext);
}
